import axios from 'axios';
import { IInvoice } from '../../interfaces';
import { normalizePaymentProvider } from '../../functions/paymentProvider';
import { noteStripeRedirect } from '../../functions/stripeReturn';

type Customer = IInvoice['customer'];

/**
 * Mirrors SignInvoiceSerializer in backend-new. The endpoint is a plain
 * Serializer with an explicit field list, so anything not listed here is
 * dropped on arrival.
 */
export type SignInvoicePayload = {
    customer_email: string;
    name: string;
    street_address: string;
    care_of: string;
    postal_code: string;
    city: string;
    intent_uuid?: string;
    need_bank_signature?: false;
};

type BuildOptions = {
    /**
     * Fields the customer edited in a form. Anything absent falls back to the
     * invoice, which is what the "approve" buttons rely on.
     */
    overrides?: Partial<Customer>;
    /**
     * Omitted means the backend applies its default of true.
     */
    needBankSignature?: false;
};

export const buildSignInvoicePayload = (
    invoice: IInvoice,
    { overrides, needBankSignature }: BuildOptions = {}
): SignInvoicePayload => {
    const customer = invoice.customer;
    const payload: SignInvoicePayload = {
        customer_email: overrides?.email ?? customer.email,
        name: overrides?.name ?? customer.name,
        street_address: overrides?.street_address ?? customer.street_address,
        care_of: overrides?.care_of ?? customer.care_of,
        postal_code: overrides?.postal_code ?? customer.postal_code,
        city: overrides?.city ?? customer.city
    };

    // The field rejects blanks, so an empty intent has to be left out entirely.
    if (invoice.intent_uuid) {
        payload.intent_uuid = invoice.intent_uuid;
    }

    if (needBankSignature === false) {
        payload.need_bank_signature = false;
    }

    return payload;
};

/**
 * Signs an invoice and sends the customer wherever the backend redirects them:
 * BankID, Stripe's payment page, or straight back to the checkout.
 */
const signInvoice = async (
    invoiceUuid: string,
    payload: SignInvoicePayload,
    paymentProvider?: string | null
): Promise<void> => {
    try {
        const res = await axios.post(
            import.meta.env.VITE_API_BASE_URL + 'web/invoice/' + invoiceUuid + '/sign_invoice/',
            payload
        );
        // Noted whether Stripe comes now or after BankID.
        if (normalizePaymentProvider(paymentProvider) === 'stripe') {
            noteStripeRedirect(invoiceUuid);
        }
        window.location.href = res.data.url;
    } catch (err) {
        console.log(err);
    }
};

export default signInvoice;
